import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import { useId } from 'react'
import { motion, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'

interface IrisProps {
    aperture: MotionValue<number>
    color?: string
    shade?: string
    seam?: string
    className?: string
}

const BLADES = 7
const REACH = 190

interface Point {
    x: number
    y: number
}

const geometry = (radius: number): { vertices: Array<Point>, tangents: Array<Point> } => {
    const turn = radius * 1.8
    const vertices: Array<Point> = []
    const tangents: Array<Point> = []

    for (let index = 0; index < BLADES; index++) {
        const angle = ((turn + (index * 360) / BLADES) * Math.PI) / 180
        const seam = angle + (72 * Math.PI) / 180

        vertices.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius })
        tangents.push({ x: Math.cos(seam), y: Math.sin(seam) })
    }

    return { vertices, tangents }
}

const round = (value: number): string => value.toFixed(3)

const holePath = (radius: number): string => {
    const { vertices } = geometry(radius)

    return `M ${vertices.map(point => `${round(point.x)} ${round(point.y)}`).join(' L ')} Z`
}

const bladePath = (radius: number, parity: number): string => {
    const { vertices, tangents } = geometry(radius)
    let path = ''

    for (let index = parity; index < BLADES; index += 2) {
        const next = (index + 1) % BLADES
        const a = vertices[index]
        const b = vertices[next]
        const outerA = { x: a.x + tangents[index].x * REACH, y: a.y + tangents[index].y * REACH }
        const outerB = { x: b.x + tangents[next].x * REACH, y: b.y + tangents[next].y * REACH }

        path += `M ${round(a.x)} ${round(a.y)} L ${round(outerA.x)} ${round(outerA.y)} L ${round(outerB.x)} ${round(outerB.y)} L ${round(b.x)} ${round(b.y)} Z `
    }

    return path
}

const seamPath = (radius: number): string => {
    const { vertices, tangents } = geometry(radius)

    return vertices.map((point, index) => {
        const end = { x: point.x + tangents[index].x * REACH, y: point.y + tangents[index].y * REACH }

        return `M ${round(point.x)} ${round(point.y)} L ${round(end.x)} ${round(end.y)}`
    }).join(' ')
}

export const IRIS_OPEN = 82

export const Iris: FC<IrisProps> = ({
    aperture,
    color = '#0e0e0b',
    shade = '#151410',
    seam = 'rgba(236, 230, 218, 0.16)',
    className
}): ReactNode => {
    const id = `iris${useId().replace(/[^a-zA-Z0-9]/g, '')}`

    const hole = useTransform(aperture, value => holePath(Math.max(0, value)))
    const even = useTransform(aperture, value => bladePath(Math.max(0, value), 0))
    const odd = useTransform(aperture, value => bladePath(Math.max(0, value), 1))
    const seams = useTransform(aperture, value => seamPath(Math.max(0, value)))
    const visibility = useTransform(aperture, value => (value >= IRIS_OPEN - 1 ? 'hidden' : 'visible'))

    return (
        <motion.svg
            aria-hidden='true'
            viewBox='-50 -50 100 100'
            preserveAspectRatio='xMidYMid slice'
            className={cn('pointer-events-none absolute inset-0 h-full w-full', className)}
            style={{ visibility }}
        >
            <defs>
                <mask id={id} maskUnits='userSpaceOnUse' x='-100' y='-100' width='200' height='200'>
                    <rect x='-100' y='-100' width='200' height='200' fill='white' />
                    <motion.path d={hole} fill='black' />
                </mask>
            </defs>

            <rect x='-100' y='-100' width='200' height='200' fill={color} mask={`url(#${id})`} />
            <motion.path d={even} fill={color} />
            <motion.path d={odd} fill={shade} />
            <motion.path d={seams} fill='none' stroke={seam} strokeWidth='0.18' />
        </motion.svg>
    )
}
